import { useEffect, useState } from 'react';

import { AlairasMezo } from './AlairasMezo.tsx';

/**
 * Aláírás külön lapon. (2026-10-04, a sofőrök visszajelzése)
 *
 * Korábban a rajzfelület az űrlap közepén állt. A rajzoláshoz a vászonnak
 * el kell nyelnie a böngésző mozdulatait — e nélkül nem lehetne rajzolni —,
 * ezért **minden húzás, ami a vásznon indult, rajz lett, nem görgetés**.
 * A sofőrök így görgetés közben folyton belerajzoltak az aláírásba.
 *
 * Ezért az űrlapon már csak egy sor van: a gomb (vagy a kész aláírás
 * bélyegképe). A rajzfelület teljes képernyőn nyílik, ahol nincs mit
 * görgetni — így a hibalehetőség nem csökken, hanem megszűnik. Ráadásul
 * a telefon teljes magasságában lehet aláírni a korábbi keskeny csík
 * helyett.
 */
export function AlairasGomb({
  cimke,
  leiras,
  ertek,
  onValtozott,
}: {
  cimke: string;
  /** Egy mondat a gomb alá, ha kell magyarázat. */
  leiras?: string;
  /** A mostani aláírás (adat-URL), vagy null. */
  ertek: string | null;
  onValtozott: (kep: string | null) => void;
}) {
  const [nyitva, setNyitva] = useState(false);
  const [rajz, setRajz] = useState<string | null>(ertek);
  /* A vászon annyi helyet kap, amennyi a fejléc és a gombsor után marad. */
  const [magas, setMagas] = useState(() => vaszonMagassag());

  /**
   * A vászon a maradék helyet kapja: a lap fejléce és gombsora 137, a
   * belső keret 28, a mező felirata és az aláírásvonal 59 képpont.
   * Telefonon így kb. háromszor akkora felületen írnak alá, mint korábban.
   */
  function vaszonMagassag(): number {
    if (typeof window === 'undefined') return 300;
    return Math.max(180, Math.min(620, window.innerHeight - 225));
  }

  useEffect(() => {
    if (!nyitva) return;

    const ujraMer = () => setMagas(vaszonMagassag());
    const billentyu = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNyitva(false);
    };
    window.addEventListener('resize', ujraMer);
    window.addEventListener('orientationchange', ujraMer);
    window.addEventListener('keydown', billentyu);

    /* Amíg a lap nyitva van, a mögötte levő űrlap ne mozogjon. */
    const regi = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('resize', ujraMer);
      window.removeEventListener('orientationchange', ujraMer);
      window.removeEventListener('keydown', billentyu);
      document.body.style.overflow = regi;
    };
  }, [nyitva]);

  function megnyit() {
    setRajz(ertek);
    setMagas(vaszonMagassag());
    setNyitva(true);
  }

  function kesz() {
    onValtozott(rajz);
    setNyitva(false);
  }

  return (
    <>
      <div className="alsor">
        <div className="alsorfej">
          <span className="lbl">{cimke}</span>
          {ertek ? (
            <span className="alkesz">aláírva</span>
          ) : (
            <span className="c">még nincs aláírva</span>
          )}
        </div>

        {ertek && <img className="alkep" src={ertek} alt={cimke} />}

        <div className="btns">
          <button className="btn sm" type="button" onClick={megnyit}>
            {ertek ? 'Újra aláírás' : 'Aláírás'}
          </button>
          {ertek && (
            <button className="btn sm" type="button" onClick={() => onValtozott(null)}>
              Törlés
            </button>
          )}
        </div>

        {leiras && <div className="c">{leiras}</div>}
      </div>

      {nyitva && (
        <div className="allap">
          <div className="allapfej">
            <b>{cimke}</b>
            <button className="btn sm" type="button" onClick={() => setNyitva(false)}>
              Mégse
            </button>
          </div>

          <div className="allaptest">
            <AlairasMezo cimke="Írj alá az ujjaddal" kezdoKep={ertek} magassag={magas} onValtozott={setRajz} />
          </div>

          <div className="allaplab">
            <button className="btn pri" type="button" disabled={!rajz} onClick={kesz}>
              Kész
            </button>
          </div>
        </div>
      )}
    </>
  );
}
